// English UI dictionary
const en = {
  meta: {
    title: "LedgerLens — Receipt OCR for business teams",
    description:
      "Every receipt scanned, every cent of VAT accounted for. LedgerLens reads your receipts automatically, breaks down VAT and calculates employee refunds.",
    ogTitle: "LedgerLens",
    ogDescription: "Every receipt scanned, every cent of VAT accounted for.",
    imageAlt: "LedgerLens — receipt OCR and VAT bookkeeping",
    locale: "en_US",
  },
  common: {
    appName: "LedgerLens",
    save: "Save", saving: "Saving…", cancel: "Cancel", close: "Close", delete: "Delete",
    edit: "Edit", approve: "Approve", reject: "Reject", rescan: "Re-scan", back: "Back",
    continue: "Continue", loading: "Loading…", search: "Search", clear: "Clear", reset: "Reset",
    export: "Export", all: "All", yes: "Yes", no: "No", actions: "Actions", status: "Status",
    date: "Date", total: "Total", vat: "VAT", net: "Net", gross: "Gross",
    refund: "Refund", category: "Category", employee: "Partner / Driver", vendor: "Vendor",
    receiptNo: "Receipt no.", currency: "Currency", language: "Language", signIn: "Sign in",
    signOut: "Sign out", email: "Email", password: "Password", name: "Full name", role: "Role",
    admin: "Administrator", employeeRole: "Partner / Driver", accountantRole: "Accountant",
    seats: "Seats", plan: "Plan", upgrade: "Upgrade", send: "Send", sending: "Sending…",
    recipients: "Recipients", message: "Message", optional: "optional", copy: "Copy",
    revoke: "Revoke", resend: "Resend", dismiss: "Dismiss", download: "Download",
    active: "Active", disabled: "Disabled",
    from: "From", to: "To", receipts: "Receipts", none: "None", note: "Note", you: "you",
    unlimitedSeats: "Unlimited",
    free: "Free", perMonth: "/ month", of: "of", tryAgain: "Please try again",
  },
  nav: {
    dashboard: "Dashboard", scan: "Scan receipts", receipts: "Receipts",
    duplicates: "Duplicates", reports: "Reports", team: "Team", settings: "Settings",
    billing: "Billing", myScan: "Scan", myReceipts: "My receipts", myRefunds: "Refunds",
    readOnly: "Read-only", audit: "Audit log",
  },
  audit: {
    title: "Audit log",
    sub: "Who changed what and when — the full trail for your company",
    action: "Action", actor: "User", entity: "Object", when: "Time", details: "Details",
    searchPlaceholder: "Name, action or ID…",
    count: "Showing {shown} of {total} events",
    loadMore: "Load more",
    emptyTitle: "The audit log is empty",
    emptyBody: "Every change to receipts, rules and your team is recorded here automatically.",
    emptyFiltered: "No events match the selected filters.",
    entities: {
      receipt: "Receipt", tenant: "Company", user: "User", report: "Report",
      invite: "Invite", subscription: "Subscription",
    },
    actions: {
      tenant_created: "Company created",
      invite_created: "Invite sent",
      invite_accepted: "Invite accepted",
      receipt_uploaded: "Receipt uploaded",
      receipt_parsed: "Receipt parsed (OCR)",
      receipt_edited: "Receipt edited",
      receipt_approved: "Receipt approved",
      receipt_bulk_approved: "Receipts bulk approved",
      receipt_rejected: "Receipt rejected",
      receipt_reprocess: "Receipt re-scanned",
      receipt_deleted: "Receipt deleted",
      duplicate_kept: "Duplicate kept",
      duplicate_confirmed: "Duplicate confirmed",
      duplicate_deleted: "Duplicate deleted",
      settings_company_updated: "Company details updated",
      settings_vat_rates_updated: "VAT rates updated",
      settings_categories_updated: "Categories updated",
      settings_refund_rules_updated: "Refund rules updated",
      notifications_updated: "Notification settings updated",
      team_member_updated: "Team member updated",
      team_member_removed: "Team member removed",
      team_refund_override_set: "Personal refund rule set",
      team_refund_override_cleared: "Personal refund rule cleared",
      report_exported: "Report exported",
      report_emailed: "Report emailed",
      report_monthly_sent: "Monthly report sent",
      billing_checkout_started: "Checkout started",
      billing_subscription_activated: "Subscription activated",
      billing_seats_synced: "Seats synced",
    },
  },
  status: {
    queued: "Queued", processing: "Scanning", parsed: "Parsed", needs_review: "Needs review",
    failed: "Failed", approved: "Approved", rejected: "Rejected", uploading: "Uploading",
  },
  landing: {
    badge: "OCR receipt scanning for B2B expense teams",
    h1: "Every receipt scanned, every cent of VAT accounted for.",
    sub: "LedgerLens turns receipt photos into structured expense data in seconds: totals, receipt numbers, dates, vendors and the complete VAT breakdown — then calculates exactly what you owe back to your drivers and partners.",
    ctaPrimary: "Create company account", ctaSecondary: "Sign in",
    trialNote: "14-day free trial · 50 scans included · no card required",
    audience1: "Transport & fleets", audience2: "Trades & contractors",
    audience3: "Accounting offices",
    liveExtraction: "Live extraction",
    howTitle: "How it works",
    step: "Step",
    step1Title: "Capture",
    step1Body: "Drivers and field staff snap a photo of any receipt — fuel, tires, parts, tools, hotels, services.",
    step2Title: "Automatic OCR",
    step2Body: "Vendor, receipt number, date, totals and the full VAT breakdown per rate are extracted in seconds.",
    step3Title: "VAT & refunds",
    step3Body: "Company totals and reclaimable VAT update live, and each worker's refund is calculated by your own rules.",
    featuresTitle: "Built for multi-company operations",
    f1Title: "Strict tenant isolation",
    f1Body: "Every record carries a tenant ID and every query is scoped to it — companies never see each other's data.",
    f2Title: "Live admin dashboard",
    f2Body: "Scanned receipts appear on the manager's screen instantly with all extracted parameters and cumulative VAT.",
    f3Title: "Configurable refunds",
    f3Body: "Give back a custom percentage of VAT or of the total, with per-category and per-employee rules and per-receipt caps.",
    f4Title: "Accounting exports",
    f4Body: "Clean PDF and CSV expense and VAT summaries your accountant can file without retyping anything.",
    pricingTitle: "Pricing that follows your team",
    pricingSub: "A monthly platform fee plus a price per active user, with included scan volume and transparent per-scan overage.",
    pricingCta: "Start free trial",
    monthlyIncluded: "scans included",
    faqTitle: "Frequently asked",
    faq1Q: "Which receipts can it read?",
    faq1A: "Any business receipt or invoice — fuel, tires, car parts, workshop services, tools, office supplies, hotels, tolls and general services — in most European languages.",
    faq2Q: "How is VAT handled?",
    faq2A: "Each receipt is split per VAT rate (25%, 13%, 5%, 0% by default, fully editable per company) and the net base, VAT and gross amounts are reconciled against the printed totals.",
    faq3Q: "How do employee refunds work?",
    faq3A: "You decide: a percentage of the VAT, a percentage of the total, or both — with overrides per expense category and per employee, and a cap per receipt.",
    faq4Q: "How is my company data separated?",
    faq4A: "Every document is written with your tenant ID and every read is filtered by it in the data layer, so cross-company access is impossible by design.",
    footerRights: "multi-tenant expense & VAT platform",
    demoCategory: "Fuel",
    unlimitedUsers: "Unlimited users",
    extraScan: "{price} per extra scan",
    upToSeats: "Up to {n} users",
    seatPrice: "+ {price} per user",
    legalTerms: "Terms of Service", legalPrivacy: "Privacy Policy",
    legalRefund: "Refund Policy",
  },
  auth: {
    loginTitle: "Sign in",
    loginSub: "Company admins and field staff use the same login.",
    heroTitle: "Receipts in. VAT, totals and refunds out.",
    heroSub: "Managers see every scanned receipt the moment it lands, with the full VAT breakdown and the refund each worker has earned.",
    heroFooter: "Strict tenant isolation · EU VAT rates · PDF & CSV exports",
    workEmail: "Work email",
    demoTitle: "Explore with demo data",
    demoSub: "Opens a ready-made demo company so you can try scanning immediately.",
    demoAdmin: "Demo admin", demoDriver: "Demo driver", demoAccountant: "Demo accountant",
    newCompany: "New company?", createAccount: "Create an account",
    welcomeBack: "Welcome back",
    orgTypeLabel: "What kind of organisation are you?",
    orgTypeHint: "We tailor the app to you — you can change this later in Settings.",
    orgType_fleet_owner: "Fleet owner / Aggregator",
    orgType_fleet_owner_hint: "Vehicles and drivers, refunds and reports for your accountant.",
    orgType_accounting_firm: "Accounting firm",
    orgType_accounting_firm_hint: "You are the accountant — direct Synesis / Minimax exports and VAT.",
    signupStep1: "1. Company", signupStep2: "2. Admin account",
    signupTitle1: "Tell us about your company", signupTitle2: "Create your admin login",
    signupSub: "Your company gets an isolated workspace. You can invite drivers, technicians and partners right after.",
    companyName: "Company name", country: "Country", taxId: "Company tax ID / VAT number (optional)",
    yourName: "Your full name", passwordHint: "Password (min. 6 characters)",
    createCompany: "Create company", alreadyHave: "Already have an account? Sign in",
    companyCreated: "Company created — 14-day trial started",
    inviteJoin: "Join", invitedAs: "You were invited as",
    choosePassword: "Choose a password", joinCompany: "Join company",
    inviteInvalid: "This invitation is no longer valid", goToSignIn: "Go to sign in",
    welcomeTo: "Welcome to",
  },
  dash: {
    title: "Dashboard", sub: "Live expense, VAT and refund overview",
    range30: "Last 30 days", range90: "Last 90 days", range365: "Last 12 months",
    rangeAll: "All time",
    scanCta: "Scan receipt",
    kpiExpenses: "Total expenses", kpiExpensesHint: "Gross total of parsed, reviewed and approved receipts in the selected period.",
    kpiExpensesSub: "receipts counted",
    kpiVat: "Reclaimable VAT", kpiVatHint: "Cumulative VAT contained in the receipts, split per rate in the chart below.",
    kpiRefunds: "Refunds owed", kpiRefundsHint: "What your workers and partners have earned back based on the configured percentages.",
    kpiRefundsSub: "per your refund rules",
    kpiUnverified: "Unverified receipts",
    kpiReview: "Needs review", kpiReviewHint: "Receipts where the OCR confidence was low or the VAT math did not reconcile.",
    kpiScans: "Scans this month", kpiScansHint: "Scan volume included in your plan for the current billing month.",
    scanning: "scanning", failed: "failed",
    feedTitle: "Live receipt feed", feedIdle: "Newly scanned receipts appear here automatically",
    feedActive: "being scanned right now…", viewAll: "View all",
    emptyTitle: "No receipts yet",
    emptyBody: "Scan your first receipt — vendor, totals and the VAT breakdown are extracted automatically.",
    vatByRate: "VAT by rate", vatByRateSub: "Net base and VAT per rate on your receipts",
    netBase: "Net base",
    spendByCategory: "Spend by category", spendOverTime: "Spend over time",
    refundsByEmployee: "Refunds by employee",
    noVatData: "No VAT data in this period", noCategoryData: "No category data yet",
    noReceiptsPeriod: "No receipts in this period",
    noRefunds: "Refund totals appear once receipts are scanned.",
    vendorEmployee: "Vendor / partner",
    trialWarning: "You have used {used} of {total} trial scans",
    choosePlan: "Choose a plan",
    duplicatesWarning: "{count} possible duplicate(s) waiting for review",
    reviewDuplicates: "Review duplicates",
  },
  receipts: {
    payoutEmail: "Email",
    payoutTitle: "Refund payouts",
    payoutPaid: "Paid out",
    payoutUnpaid: "Awaiting payout",
    markPaid: "Mark as paid",
    markUnpaid: "Undo payout",
    paidBadge: "Paid",
    unpaidBadge: "Awaiting payout",
    paidToast: "Refund marked as paid",
    paidBulkToast: "{count} refund(s) marked as paid ({amount})",
    unpaidToast: "Payout reverted",
    paidOn: "Paid {when} · {who}",
    payoutFilter: "Payout",
    payoutAll: "All",
    exportLayout: "Accounting layout",
    exportMenu: "Export",
    title: "Receipts", subCount: "{count} receipt(s) matching your filters",
    searchPlaceholder: "Search vendor, receipt number, employee…",
    allCategories: "All categories", everyone: "Everyone",
    selected: "{count} selected", approveSelected: "Approve selected", exportCsv: "Export CSV",
    emptyTitle: "No receipts yet", emptyFiltered: "No receipts match these filters",
    emptyBody: "Upload or snap your first receipt to start tracking expenses and VAT.",
    emptyBodyFiltered: "Try clearing the filters or widening the date range.",
    clearFilters: "Clear filters", scanCta: "Scan a receipt",
    filterDuplicates: "Duplicates",
  },
  drawer: {
    uploaded: "uploaded", employeeRefund: "Employee refund",
    ofVat: "{pct}% of VAT", ofGross: "{pct}% of gross",
    both: "{vat}% of VAT + {gross}% of gross", categoryRule: "category rule",
    employeeRule: "employee rule", capped: "capped",
    litres: "Litres", odometer: "Odometer",
    checkBefore: "Check these before approving", vatReconciles: "VAT figures reconcile with the printed totals",
    duplicateWarning: "This image matches a receipt already uploaded.",
    vendorField: "Vendor", taxIdField: "Vendor tax ID", numberField: "Receipt number",
    dateField: "Issue date", paymentField: "Payment method",
    netField: "Net total", vatField: "VAT total", grossField: "Gross total",
    vatRecap: "VAT recapitulation", rate: "Rate", noRates: "No VAT rates detected",
    lineItems: "Line items", internalNote: "Internal note",
    notePlaceholder: "e.g. vehicle ZG-1234-AB, job #4471",
    saveChanges: "Save changes", activity: "Activity", loadingImage: "Loading image…",
    readOnly: "Accountant access is read-only.",
    savedToast: "Receipt updated", refundRecalculated: "Refund recalculated",
    approvedToast: "Receipt approved", rejectedToast: "Receipt rejected", deletedToast: "Receipt deleted",
    rescanToast: "Re-scanning receipt…",
  },
  scan: {
    title: "Scan receipts",
    sub: "Upload or photograph any business receipt — OCR extracts totals and VAT automatically",
    dropTitle: "Snap or drop your business receipts",
    splitPdf: "Each page is a separate receipt",
    splitPdfHint: "Turn on if the PDF is a bundle of receipts. Off: a multi-page PDF is read as one invoice.",
    dropSub: "Fuel, tires, parts, tools, office supplies, services — any receipt. JPG, PNG, WEBP or PDF, up to 20 at once.",
    unsupportedFiles: "{count} file(s) skipped — JPG, PNG, WEBP and PDF are supported.",
    takePhoto: "Take photo", chooseFiles: "Choose files",
    sampleTitle: "No receipt at hand? Scan a real example",
    tipsTitle: "Tips for a clean scan",
    tip1: "Lay the receipt flat and fill the frame — all four corners visible.",
    tip2: "Avoid shadows and glare; daylight works best.",
    tip3: "Long receipts: keep the totals and VAT recapitulation section in the shot.",
    recentTitle: "Recently scanned", nothingScanned: "Nothing scanned yet.",
    allReceipts: "All receipts", readingOcr: "Reading receipt with OCR…",
    scanningOne: "Scanning receipt…", scanningMany: "Scanning {count} receipts…",
    scanningSample: "Scanning example receipt…",
    blockedTitle: "Monthly scan quota reached",
    blockedBody: "You have used {used} of {total} scans on the {plan} plan. Choose a bigger plan to keep scanning.",
    viewPlans: "View plans",
    remaining: "{count} scan(s) left this month",
    overage: "Overage billed at {price} per scan",
    couldNotRead: "Could not read {name}", tryBetterPhoto: "Try a sharper, well-lit photo.",
    needsReviewToast: "{vendor} needs review",
  },
  duplicates: {
    title: "Duplicate review",
    sub: "Possible double uploads or suspicious receipts — you decide what stays in the totals",
    emptyTitle: "No duplicates to review",
    emptyBody: "When the system spots the same image or the same receipt (same vendor, date and amount), it will appear here.",
    reasonImage: "The exact same photo was uploaded before",
    reasonSame: "Same vendor, date and amount as an existing receipt",
    newReceipt: "New receipt", originalReceipt: "Existing receipt",
    keepBoth: "Not a duplicate — keep", confirmDup: "Confirm duplicate (reject)",
    deleteScan: "Delete scan",
    keptToast: "Receipt kept in the totals", confirmedToast: "Duplicate rejected — excluded from totals",
    deletedToast: "Scan deleted",
    uploadedBy: "Uploaded by", pending: "{count} to review",
  },
  reports: {
    title: "Reports", sub: "Expense and VAT summaries ready for your accountant",
    employee: "Partner / Driver", include: "Include", includeAll: "All countable receipts",
    includeApproved: "Approved only", resetFilters: "Reset",
    receiptsCount: "Receipts", netTotal: "Net total", vatTotal: "VAT total",
    grossTotal: "Gross total", refunds: "Refunds",
    vatRecap: "VAT recapitulation by rate", refundsPerEmployee: "Refunds per employee",
    receiptDetail: "Receipt detail", nothingTitle: "Nothing to report yet",
    nothingBody: "Scan receipts or widen the date range to build an accounting report.",
    periodInfo: "{count} receipt(s) · period {period}",
    emailReport: "Email to accountant",
    emailTitle: "Email the summary",
    emailSub: "The report is sent as a PDF and/or CSV attachment for the selected period.",
    addRecipient: "Add recipient (email)", attachments: "Attachments",
    sentToast: "Report sent to {count} recipient(s)",
    monthlyTitle: "Automatic monthly summary",
    monthlySub: "On the configured day the system emails last month's summary.",
    sendNow: "Send last month now", history: "Send history",
    downloadedCsv: "CSV report downloaded", downloadedPdf: "PDF report downloaded",
    vatRate: "VAT rate",
    noVatData: "No VAT data for this selection.",
    presetUniversal: "Universal CSV",
    presetSynesis: "Synesis (eURA.txt)",
    presetMinimax: "Minimax (Minimax_URA.csv)",
    presetHint: "Universal CSV (;) includes a receipt link. Synesis exports a tab-separated eURA.txt and Minimax a semicolon Minimax_URA.csv — both in their strict layout with DD.MM.YYYY dates.",
    emptyExport: "No receipts in the selected period — the CSV contains only the header row.",
    accountingExports: "Accounting exports",
    downloadPdf: "Download PDF",
    pdfStandard: "PDF",
    pdfWithAttachments: "PDF with images",
    payoutTitle: "Owed to employees",
    payoutSub: "What to transfer to each employee for the selected period (refunds not yet paid out).",
    payoutEmail: "Email",
    payoutOwed: "To transfer",
    payoutExport: "Export payout list",
    payoutDownloaded: "Payout list downloaded",
    payoutEmpty: "No refunds awaiting payout in the selected period.",
    accountingPdf: "PDF with images",
    accountingPdfHint: "Cover page with the summary and VAT plus one page per receipt (image + data).",
    exportQueued: "Your export is being prepared — we will email the link to {email}.",
    exportReady: "Your export is ready",
    exportFailed: "The export could not be prepared",
    docsTitle: "Issued documents",
    docsSub: "Every export and emailed report gets its own document number (e.g. IZV-2026-0001).",
    docNumber: "Document no.",
    docKind: "Type",
    docPeriod: "Period",
    docBy: "Issued by",
    docsEmpty: "No documents issued yet — export or email a report and a number is assigned automatically.",
    docKinds: {
      export_csv: "CSV export",
      export_pdf: "PDF export",
      report: "Emailed report",
      monthly_report: "Monthly report",
    },
  },
  team: {
    title: "Team", sub: "Invite drivers, technicians, partners and your accountant",
    invitePerson: "Invite person", inviteTitle: "Invite to your company",
    inviteSub: "They receive an email with a link to set a password. Partners / drivers can only upload receipts and see their own history.",
    inviteEmail: "Email", inviteName: "Name (optional)", inviteRole: "Role",
    createInvite: "Create invitation", inviteCreated: "Invitation created",
    inviteCopied: "The invite link was copied to your clipboard.",
    inviteEmailed: "Invitation emailed to {email}",
    seatsInUse: "Seats in use", pendingInvites: "{count} pending invitation(s)",
    seatFree: "Accountants never use a seat",
    rolePermissions: "Role permissions",
    adminPerm: "Company admin",
    adminPermBody: "Full dashboard, all receipts, approvals, VAT & refund settings, reports and billing.",
    employeePerm: "Partner / Driver",
    employeePermBody: "Mobile capture only: snap receipts, follow their status and see their own refunds.",
    accountantPerm: "Accountant (read-only)",
    accountantPermBody: "Sees all receipts and reports, exports PDF/CSV — cannot edit, approve or change settings.",
    pendingTitle: "Pending invitations", copyLink: "Copy link",
    membersTitle: "Members", colExpenses: "Expenses", colAccess: "Access",
    colOverride: "Refund rule", colRemove: "Remove",
    overrideSet: "Custom rule", overrideNone: "Company rule",
    overrideButton: "Edit rule",
    roleChanged: "{name} is now {role}",
    accessDisabled: "Access disabled", accessRestored: "Access restored",
    removed: "{name} removed",
  },
  override: {
    title: "Refund rule for {name}",
    sub: "A personal rule takes priority over the company default and category rules.",
    enable: "Use a custom rule for this person",
    basis: "Refund basis", vatPct: "% of VAT", grossPct: "% of gross",
    cap: "Cap per receipt", note: "Note (optional)",
    example: "Example — a {gross} receipt containing {vat} VAT refunds",
    saved: "Refund rule saved ({count} receipt(s) recalculated)",
    removed: "Custom rule removed",
    remove: "Remove custom rule",
  },
  settings: {
    title: "Settings", sub: "VAT rates, refund rules, categories, notifications and company profile",
    tabRefunds: "Refund rules", tabVat: "VAT rates", tabCategories: "Categories",
    orgTypeSub: "Controls which features we show. An accounting firm does not see internal driver payouts or \"send to accountant\".",
    orgTypeSaved: "Organisation type updated",
    tabNotifications: "Notifications", tabCompany: "Company",
    companyDefault: "Company default",
    companyDefaultSub: "How much of each receipt goes back to the employee or partner who paid for it.",
    refundBasis: "Refund basis", modeVat: "Percentage of the VAT", modeGross: "Percentage of the total",
    modeBoth: "Both (VAT % + total %)",
    ofVatPct: "% of VAT", ofTotalPct: "% of total", capPerReceipt: "Cap per receipt",
    noCap: "no cap",
    example: "Example — a {gross} receipt containing {vat} VAT refunds",
    saveRecalculate: "Save & recalculate",
    perCategory: "Per-category rules",
    perCategorySub: "Override the default for specific expense types (e.g. 100% of fuel VAT, nothing for meals).",
    addCategoryRule: "Add category rule…",
    noOverrides: "No overrides — every category follows the company default.",
    colBasis: "Basis", colExample: "Example",
    vatRatesTitle: "VAT rates",
    vatRatesSub: "The rates your receipts can contain. Defaults follow EU standard/reduced rates.",
    addRate: "Add rate", colPercent: "Percent", colActive: "Active",
    saveVat: "Save VAT rates",
    categoriesTitle: "Expense categories", addCategory: "Add category",
    colLabel: "Label", colKey: "Key", colColour: "Colour", saveCategories: "Save categories",
    companyProfile: "Company profile", addressField: "Address",
    autoApprove: "Auto-approve clean receipts",
    autoApproveSub: "Receipts whose VAT reconciles and whose confidence is high skip manual review.",
    allowOverage: "Allow billed overage above the quota",
    allowOverageSub: "When on, scanning continues past the included volume and extra scans are billed. When off, scanning is blocked at the quota.",
    saveProfile: "Save profile",
    notifTitle: "Email notifications",
    notifSub: "Automatic messages to admins, employees and your accountant.",
    emailOn: "Email is configured — messages are delivered through SendGrid ({sender}).",
    emailOff: "Email is not configured — messages are logged but not delivered. Add SENDGRID_API_KEY to backend/.env.",
    notifInvites: "Team invitations", notifInvitesSub: "Send the password setup link to the invited person.",
    notifReview: "Receipts needing review", notifReviewSub: "Alert admins when a receipt requires review.",
    notifQuota: "Quota warnings", notifQuotaSub: "Notify at 80% and 100% of the monthly scan quota.",
    monthlyReport: "Monthly summary to accountant",
    monthlyReportSub: "Automatically email last month's summary to the chosen recipients.",
    monthlyDay: "Day of month", monthlyRecipients: "Recipients (comma separated)",
    monthlyFormats: "Attachment formats",
    sendTest: "Send test email", testSent: "Test email sent",
    emailLog: "Email delivery log", logTo: "Recipient", logSubject: "Subject",
    logKind: "Type", logStatus: "Status", noEmails: "No emails yet.",
    logDelivery: "Delivery",
    webhookOn: "The SendGrid webhook is connected — delivery, opens and bounces update automatically.",
    webhookOff: "Add SENDGRID_WEBHOOK_PUBLIC_KEY to backend/.env and the webhook in SendGrid to see delivery, opens and bounces.",
    webhookBadgeOn: "Delivery tracking on",
    webhookBadgeOff: "Delivery tracking not set up",
    opensShort: "{n}× opened",
    clicksShort: "{n}× clicked",
    delivery: {
      processed: "Processed", deferred: "Deferred", delivered: "Delivered",
      open: "Opened", click: "Clicked", bounce: "Bounced", blocked: "Blocked",
      dropped: "Dropped", spamreport: "Spam report", unsubscribe: "Unsubscribed",
      group_unsubscribe: "Group unsubscribe", group_resubscribe: "Group resubscribe",
    },
    saveNotifications: "Save notifications",
    savedCompany: "Company profile saved", savedVat: "VAT rates saved",
    savedCategories: "Categories saved",
    savedRules: "Refund rules saved", recomputed: "{count} receipt(s) recalculated",
    savedNotifications: "Notification settings saved",
  },
  billing: {
    seatsHint: "Seats are counted automatically from your active users.",
    statuses: { trialing: "Trialing", active: "Active", past_due: "Past due", canceled: "Cancelled", incomplete: "Incomplete", unpaid: "Unpaid" },
    title: "Billing & subscription",
    sub: "Seat-based pricing with included scan volume and transparent overage",
    modeUnconfiguredTitle: "Billing is not configured yet",
    modeUnconfiguredBody: "Add your Stripe secret key as STRIPE_API_KEY on the server to enable subscriptions. Everything else keeps working.",
    modeTestTitle: "Stripe test mode",
    modeTestBody: "Checkout runs against the platform test key, so subscriptions are simulated end to end. Plug in your own Stripe secret key to go live and unlock the customer portal and seat sync.",
    modeByokTitle: "Connected to your Stripe account",
    modeByokBody: "Subscriptions, the customer portal and automatic seat sync all run on your own Stripe keys.",
    currentPlan: "Current plan", statusLabel: "Status", trialEnds: "Trial ends",
    scansThisMonth: "Scans this month", billingPeriod: "Billing period",
    activeSeats: "Active seats", syncSeats: "Sync seat count",
    thisMonthUsage: "This month at current usage",
    platformFee: "Platform fee", seatsWord: "seat(s)", extraScans: "extra scan(s)",
    noOverage: "no overage",
    choosePlan: "Choose a plan",
    choosePlanSub: "Seat count is billed monthly; extra scans beyond the included volume are charged per scan.",
    seatsToBuy: "Seats to buy", popular: "Popular", current: "Current",
    subscribeTo: "Subscribe to {plan}", updateSubscription: "Update subscription",
    includedOnSignup: "Included on signup", perExtraScan: "{price} per extra scan",
    checkoutHistory: "Checkout history", colStarted: "Started", colSeats: "Seats",
    colAmount: "Amount", colPayment: "Payment", portal: "Customer portal",
    redirecting: "Redirecting to secure Stripe checkout…",
    seatsSynced: "Seat count set to {count}",
    overageOn: "Overage allowed", overageOff: "Overage blocked",
  },
  employee: {
    greeting: "Hi {name}", sub: "Snap a receipt and it is logged for the office instantly",
    myExpenses: "My expenses", myRefund: "My refund", approvedWord: "approved",
    lastReceipts: "My last receipts", seeAll: "See all",
    nothingYet: "Nothing yet — take a photo of your first receipt above.",
    refundPolicy: "Your refund policy",
    policyVat: "You get {pct}% of the VAT on each receipt.",
    policyGross: "You get {pct}% of each receipt total.",
    policyBoth: "You get {vat}% of the VAT plus {gross}% of the total.",
    categoryNote: " Some categories have their own rate.",
    myReceiptsTitle: "My receipts", myReceiptsSub: "Everything you have submitted",
    filterAll: "All", filterScanning: "Scanning", filterReview: "Needs review",
    filterApproved: "Approved",
    emptyTitle: "No receipts here yet",
    emptyBody: "Receipts you snap appear here with their status and the refund you earned.",
    refundsTitle: "My refunds", refundsSub: "What the company owes you back",
    totalEarned: "Total earned", waiting: "Waiting", byMonth: "Refunds by month",
    monthlyDetail: "Monthly detail", spent: "Spent", noRefunds: "No refunds yet",
    startScanning: "Scan receipts to start earning refunds.",
    datePending: "date pending",
    myRuleTitle: "My refund rule",
    myRuleSub: "How your refund is calculated",
    myRuleCustom: "Personal rule",
    myRuleCompany: "Company rule",
    myRuleCustomNote: "Your admin set a personal rate for you — different from the standard one.",
    myRuleCompanyNote: "The standard company rule applies to you.",
    ruleBase: "Base rate",
    ruleCap: "Max {amount} per receipt",
    ruleNoCap: "No cap per receipt",
    ruleApproval: "Refunds are paid out after an admin approves the receipt.",
    ruleNoApproval: "Refunds are counted automatically, no approval needed.",
    ruleByCategory: "Rates by category",
    ruleSpecialHint: "These categories use a different rate than your base rate.",
    ruleShowAll: "Show all categories",
    ruleHideAll: "Hide categories",
    ruleAdminNote: "Admin note",
    ruleVatShort: "{pct} % of VAT",
    ruleGrossShort: "{pct} % of the total",
    ruleBothShort: "{vat} % of VAT + {gross} % of the total",
    ruleExample: "Example: on a {gross} receipt with {vat} VAT you get {refund}.",
  },
  alerts: {
    title: "Notifications",
    bell: "Open notifications",
    unread: "{n} unread",
    markAll: "Mark all as read",
    emptyTitle: "No notifications",
    emptyBody: "Receipts needing review, approvals, quota alerts and reports show up here.",
    onlyUnread: "Unread only",
    justNow: "just now",
    kinds: {
      refund_paid: "Refund paid out",
      refund_paid_batch: "Refunds paid out",
      trial_ending: "Trial ending soon",
      payment_failed: "Payment failed",
      receipt_needs_review: "Receipt needs review",
      receipt_duplicate: "Possible duplicate receipt",
      receipt_failed: "Scan failed",
      receipt_approved: "Receipt approved",
      receipt_rejected: "Receipt rejected",
      member_joined: "New team member",
      quota_warning: "Scan quota",
      quota_blocked: "Quota used up",
      report_sent: "Report sent",
      refund_rule_changed: "Refund rule changed",
    },
    bodies: {
      refund_paid: "{vendor} · your {refund} refund has been paid out.",
      refund_paid_batch: "{refund} of refunds has been paid out to you.",
      trial_ending: "Your subscription trial is ending soon.",
      payment_failed: "The last subscription payment failed ({amount} {currency}).",
      receipt_needs_review: "{vendor} · {amount} — uploaded by {employee}",
      receipt_duplicate: "{vendor} · {amount} looks like a receipt you already have",
      receipt_failed: "We could not read \"{filename}\". Try taking a clearer photo.",
      receipt_approved: "{vendor} · {amount} — refund {refund}",
      receipt_rejected: "{vendor} · {amount} — reason: {reason}",
      member_joined: "{name} joined the team ({email})",
      quota_warning: "{used} of {included} scans used ({pct} %) on the {plan} plan.",
      quota_blocked: "{used} of {included} scans used — scanning is paused.",
      report_sent: "The {period} report was sent to {count} recipient(s) ({recipients}).",
      refund_rule_changed: "An admin updated your personal refund rule.",
    },
  },
  plans: {
    trial: {
      name: "Free trial",
      tagline: "14 days, no card required",
      features: ["50 receipt scans", "Up to 5 users", "VAT breakdown & refunds", "CSV export"],
    },
    starter: {
      name: "Starter",
      tagline: "Small crews & single-office trades",
      features: ["150 scans / month included", "Up to 10 users",
                 "PDF + CSV accounting exports", "Refund rules per category", "Email support"],
    },
    growth: {
      name: "Growth",
      tagline: "Transport fleets & contractors",
      features: ["750 scans / month included", "Up to 50 users",
                 "Live receipt feed & approvals", "Duplicate detection", "Priority support"],
    },
    scale: {
      name: "Business",
      tagline: "Accounting offices & multi-depot groups",
      features: ["5 000 scans / month included", "Unlimited users",
                 "Unlimited report exports", "Audit log", "Dedicated onboarding"],
    },
    enterprise: {
      name: "Enterprise",
      tagline: "For large accounting offices and partner networks",
      features: ["15 000 scans / month included", "Unlimited users",
                 "Multi-company overview", "Audit log & data export API",
                 "Dedicated account manager"],
    },
  },
  clients: {
    switcher: "Clients",
    ownWorkspace: "Your firm",
    addClient: "Add client",
    clientName: "Client name",
    clientTaxId: "Client OIB (optional)",
    addHint: "Creates a separate workspace for that client — receipts, VAT and exports stay separate.",
    createCta: "Create workspace",
    created: "Client {name} created",
    switched: "Workspace switched",
  },
  legal: {
    terms: "Terms of Service", privacy: "Privacy Policy", refund: "Refund Policy",
    lastUpdated: "Last updated", backHome: "Back to home", contact: "Contact",
    otherDocs: "Other documents",
  },
};

export default en;
